import { useWorkspaceText } from '../interface-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import runtimeCopy from '@/i18n/ns/workspace-knowledge-runtime';
import editorCopy from '@/i18n/ns/workspace-editors';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCompany } from '@/company/context';
import { useWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import { announceKnowledgePagesChanged } from '../knowledge-agent-events';
import { ErrorState, LoadingState } from '../primitives';
import { errorCode, knowledgePagesApi, pagesErrorMessage } from './api';
import { acceptedSuggestionLabel, shortDate } from './format';
import { blockChanges } from './marks';
import { MarkedPage, usePagePlan } from './page-body';
import { usePageHistory } from './use-wiki';
import type { KnowledgePageRevision, PageHistoryEntry, PageView, WriteResult } from './types';

// One empty list for every render, so usePagePlan's memo holds while the reason
// box is typed in.
const NO_SUGGESTIONS: never[] = [];

/** "Accepted Poneva's suggestion", "Edited: “Added credit limits”", "Imported". */
function entrySummary(
  entry: PageHistoryEntry,
  copy: Readonly<Record<keyof typeof runtimeCopy.en, string>>,
  editor: Readonly<Record<keyof typeof editorCopy.en, string>>,
) {
  if (entry.editedByName)
    return entry.note
      ? formatCopy(editor.editedPageSummary, { note: entry.note })
      : editor.editedLabel;
  if (entry.authorKind === 'import') return copy.imported;
  return acceptedSuggestionLabel(entry, copy) ?? (entry.note ? `“${entry.note}”` : copy.saved);
}

function entryPeople(entry: PageHistoryEntry, locale: string) {
  return [entry.editedByName ?? entry.approvedByName, shortDate(entry.createdAt, locale)]
    .filter(Boolean)
    .join(' · ');
}

/** Revisions never change, so each is read once per company and kept for the session. */
const revisions = new Map<string, Promise<KnowledgePageRevision>>();
function readRevision(companyId: string, pageId: string, revisionId: string) {
  const key = `${companyId}\0${revisionId}`;
  let found = revisions.get(key);
  if (!found) {
    found = knowledgePagesApi.revision(pageId, revisionId);
    found.catch(() => revisions.delete(key));
    revisions.set(key, found);
    if (revisions.size > 100) revisions.delete(revisions.keys().next().value!);
  }
  return found;
}

export function resetRevisionCacheForTests() {
  revisions.clear();
}

function useComparison(
  pageId: string,
  entry: PageHistoryEntry | null,
  previous: PageHistoryEntry | null,
) {
  const { text: uiText } = useWorkspaceText();
  const companyId = useCompany().data?.company?.id ?? '';
  const key = entry ? `${entry.id}\0${previous?.id ?? ''}` : '';
  const [state, setState] = useState<{
    key: string;
    after: KnowledgePageRevision | null;
    before: KnowledgePageRevision | null;
    error: string;
  }>({ key: '', after: null, before: null, error: '' });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!entry || !companyId) return;
    let active = true;
    void Promise.all([
      readRevision(companyId, pageId, entry.id),
      previous ? readRevision(companyId, pageId, previous.id) : Promise.resolve(null),
    ]).then(
      ([after, before]) => {
        if (active) setState({ key, after, before, error: '' });
      },
      (failure) => {
        if (active)
          setState({
            key,
            after: null,
            before: null,
            error: pagesErrorMessage(
              failure,
              uiText('We could not load this revision.'),
              uiText.locale,
            ),
          });
      },
    );
    return () => {
      active = false;
    };
  }, [key, companyId, retry, uiText]);
  const current = state.key === key ? state : { key, after: null, before: null, error: '' };
  return { ...current, retry: () => setRetry((value) => value + 1) };
}

/**
 * Every saved revision. Selecting one shows what it changed against the one
 * before, with the same marks as a suggestion; restoring an earlier one saves
 * its text as a new revision (or, without publish rights, suggests it).
 */
export function PageHistory({
  view,
  canPublish,
  initialRevision,
  onRestored,
}: {
  view: PageView;
  canPublish: boolean;
  /** The revision selected first (a link to an exact revision). */
  initialRevision?: string;
  onRestored: (result: WriteResult) => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const locale = useChatInterfaceLocale();
  const runtime = useNamespaceCopy(runtimeCopy);
  const editor = useNamespaceCopy(editorCopy);
  const ws = useWorkspace();
  const history = usePageHistory(view.page.id);
  const [selectedId, setSelectedId] = useState<string | null>(initialRevision ?? null);
  const [reason, setReason] = useState('');
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const entries = history.revisions ?? [];
  const selected = entries.find((entry) => entry.id === selectedId) ?? entries[0] ?? null;
  const previous = selected
    ? (entries.find((entry) => entry.number < selected.number) ?? null)
    : null;
  const comparison = useComparison(view.page.id, selected, previous);
  const changes = useMemo(
    () =>
      comparison.after
        ? [
            {
              index: 0,
              changes: blockChanges(comparison.before?.markdown ?? '', comparison.after.markdown),
            },
          ]
        : [],
    [comparison.after, comparison.before],
  );
  const plan = usePagePlan(comparison.before?.markdown ?? '', NO_SUGGESTIONS, changes, false);
  useEffect(() => {
    setAsking(false);
    setReason('');
    setError('');
  }, [selected?.id]);
  if (history.error && !history.revisions)
    return <ErrorState message={history.error} onRetry={history.retry} />;
  if (!history.revisions) return <LoadingState label={uiText('Loading the history…')} />;
  if (!selected)
    return (
      <p className="ws-muted ws-page-history-empty">{uiText('This page has no saved text yet.')}</p>
    );
  const current = selected.id === view.page.currentRevisionId;
  async function restore() {
    if (!selected || !comparison.after || busy) return;
    if (!canPublish && !reason.trim()) {
      setError(uiText('Say in a few words why this revision should come back.'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const title = comparison.after.title && comparison.after.title !== view.page.title;
      const result = await knowledgePagesApi.save(view.page.id, {
        baseRevisionId: view.revision?.id ?? null,
        markdown: comparison.after.markdown,
        ...(title ? { title: comparison.after.title } : {}),
        ...(canPublish
          ? { note: `Restored revision ${selected.number}` }
          : { reason: reason.trim() }),
      });
      if (!mounted.current) return;
      announceKnowledgePagesChanged();
      ws.notify(
        result.outcome === 'suggested'
          ? uiText('Revision {value1} is suggested for a publisher to accept.', {
              value1: selected.number,
            })
          : result.outcome === 'unchanged'
            ? uiText('The page already reads like this revision.')
            : uiText('Revision {value1} is restored as revision {value2}.', {
                value1: selected.number,
                value2: result.revisionNumber ?? 'new',
              }),
        null,
        'success',
      );
      onRestored(result);
    } catch (failure) {
      // A newer revision arrived meanwhile: reload the page so a retry restores
      // against it instead of failing again.
      if (errorCode(failure) === 'revision_conflict') announceKnowledgePagesChanged();
      if (mounted.current)
        setError(
          pagesErrorMessage(
            failure,
            uiText('We could not restore this revision. Please try again.'),
            locale,
          ),
        );
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
  return (
    <section className="ws-page-history" aria-label={uiText('Revision history')}>
      <div className="ws-page-history-list" role="group" aria-label={uiText('Revisions')}>
        <p className="ws-muted">{uiText('Every saved revision is kept.')}</p>
        {entries.map((entry) => {
          const isCurrent = entry.id === view.page.currentRevisionId;
          return (
            <button
              key={entry.id}
              type="button"
              className="ws-page-history-row"
              aria-pressed={entry.id === selected.id}
              onClick={() => setSelectedId(entry.id)}
            >
              <span className="ws-page-history-number">
                {uiRich('Revision {value1} {value2}', {
                  value1: entry.number,
                  value2: isCurrent && (
                    <span className="ws-review-status">{uiText('Current')}</span>
                  ),
                })}
              </span>
              <span className="ws-page-history-summary">
                {entrySummary(entry, runtime, editor)}
              </span>
              <small>{entryPeople(entry, locale)}</small>
            </button>
          );
        })}
      </div>
      <div className="ws-page-history-compare">
        <header className="ws-page-history-head">
          <div>
            <strong>
              {previous
                ? uiText('Revision {value1} compared with revision {value2}', {
                    value1: selected.number,
                    value2: previous.number,
                  })
                : uiText('Revision {value1}, the first text of this page', {
                    value1: selected.number,
                  })}
            </strong>
            <small>{uiText('Green was added, struck through was removed.')}</small>
          </div>
          <div className="ws-actions">
            <button
              type="button"
              className="ws-text-button"
              onClick={() =>
                void knowledgePagesApi
                  .download(view.page.id, current ? undefined : selected.id)
                  .catch((failure) =>
                    ws.notify(
                      pagesErrorMessage(
                        failure,
                        uiText('We could not export this revision.'),
                        uiText.locale,
                      ),
                    ),
                  )
              }
            >
              {uiRich('{value1} Export Markdown', {
                value1: <WorkspaceIcon name="download" size={14} />,
              })}
            </button>
            {!current && !view.page.archivedAt && (
              <button
                type="button"
                className="ws-button"
                disabled={busy || !comparison.after}
                onClick={() => (canPublish ? void restore() : setAsking(true))}
              >
                {canPublish ? uiText('Restore this revision') : uiText('Suggest restoring it')}
              </button>
            )}
          </div>
        </header>
        {asking && !canPublish && (
          <div className="ws-page-history-reason">
            <label className="ws-page-field">
              <span>{uiText('Why should this revision come back?')}</span>
              <textarea
                rows={2}
                maxLength={2000}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                onInput={(event) => setReason((event.target as HTMLTextAreaElement).value)}
              />
            </label>
            <div className="ws-actions">
              <button
                type="button"
                className="ws-button primary"
                disabled={busy}
                onClick={() => void restore()}
              >
                {busy ? uiText('Sending…') : uiText('Send suggestion')}
              </button>
              <button type="button" className="ws-button" onClick={() => setAsking(false)}>
                {uiText('Cancel')}
              </button>
            </div>
          </div>
        )}
        {error && (
          <p className="ws-error" role="alert">
            {error}
          </p>
        )}
        {comparison.error ? (
          <ErrorState message={comparison.error} onRetry={comparison.retry} />
        ) : !comparison.after ? (
          <LoadingState label={uiText('Loading this revision…')} />
        ) : comparison.after.markdown === (comparison.before?.markdown ?? '') ? (
          <p className="ws-muted">
            {comparison.before && comparison.after.title !== comparison.before.title
              ? uiText('Only the title changed, from “{value1}” to “{value2}”.', {
                  value1: comparison.before.title,
                  value2: comparison.after.title,
                })
              : uiText('The text did not change in this revision.')}
          </p>
        ) : (
          <MarkedPage
            plan={plan}
            label={uiText('Changes in revision {value1}', { value1: selected.number })}
            showMarkers={false}
          />
        )}
      </div>
    </section>
  );
}
